import { useState } from 'react';
import { Modal } from '@/components/Modal/Modal';
import { Button } from '@/components/Button/Button';
import { t } from '@/lib/i18n';
import { useSaveApiKey, useBeginOAuth } from '../hooks';
import type { Connector } from '../schemas';
import styles from './ConnectorConnectModal.module.css';

interface Props {
  connector: Connector | null;
  onClose: () => void;
}

export function ConnectorConnectModal({ connector, onClose }: Props) {
  const [apiKey, setApiKey] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const saveApiKey = useSaveApiKey();
  const beginOAuth = useBeginOAuth();

  const busy = saveApiKey.isPending || beginOAuth.isPending;

  async function handleApiKeySave() {
    if (!connector) return;
    setError(null);
    try {
      await saveApiKey.mutateAsync({ connectorId: connector.id, apiKey });
      onClose();
    } catch {
      setError(t('Failed to save. Check the key and try again.'));
    }
  }

  async function handleOAuthBegin() {
    if (!connector) return;
    setError(null);
    try {
      const { authorizationUrl } = await beginOAuth.mutateAsync({
        connectorId: connector.id,
        clientId,
        clientSecret,
      });
      window.location.href = authorizationUrl;
    } catch {
      setError(t('Failed to start authorization. Check your credentials.'));
    }
  }

  if (!connector) return null;

  const apiKeyActions = (
    <>
      <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
        {t('Cancel')}
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={() => void handleApiKeySave()}
        disabled={busy || !apiKey}
      >
        {busy ? t('Saving…') : t('Save')}
      </Button>
    </>
  );

  const oauthActions = (
    <>
      <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
        {t('Cancel')}
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={() => void handleOAuthBegin()}
        disabled={busy || !clientId || !clientSecret}
      >
        {busy ? t('Redirecting…') : t(`Authorize with ${connector.name} →`)}
      </Button>
    </>
  );

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={`${t('Connect')} ${connector.name}`}
      actions={connector.authType === 'api_key' ? apiKeyActions : oauthActions}
    >
      {connector.authType === 'api_key' ? (
        <div className={styles.form}>
          <label className={styles.label}>
            {t('API Key')}
            <input
              className={styles.input}
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-..."
              autoComplete="off"
            />
          </label>
          {error && <p className={styles.error}>{error}</p>}
        </div>
      ) : (
        <div className={styles.form}>
          <label className={styles.label}>
            {t('Client ID')}
            <input
              className={styles.input}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className={styles.label}>
            {t('Client Secret')}
            <input
              className={styles.input}
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              autoComplete="off"
            />
          </label>
          {error && <p className={styles.error}>{error}</p>}
        </div>
      )}
    </Modal>
  );
}
